import {gql, useApolloClient} from '@apollo/client';
import {useEffect, useState} from 'react';

import {useIsMounted} from './useIsMounted';
import {useViewerInfo} from './useViewerInfo';

export const ORGANIZATION_QUERY = gql`
  query Organization($entityName: String) {
    entity(name: $entityName) {
      id
      organization {
        id
        name
      }
    }
  }
`;

export const ORGANIZATION_BILLING_QUERY = gql`
  query OrganizationBilling($entityName: String) {
    entity(name: $entityName) {
      id
      organization {
        id
        name
        billingUser {
          id
          username
          email
        }
        members {
          id
          username
          role
        }
      }
    }
  }
`;

export const useOrgName = ({
  entityName,
  skip,
}: {
  entityName: string;
  skip?: boolean;
}) => {
  const [orgName, setOrgName] = useState<string | null>(null);
  const isMounted = useIsMounted();
  const apolloClient = useApolloClient();

  useEffect(
    () => {
      if (skip) {
        return;
      }
      apolloClient
        .query({
          query: ORGANIZATION_QUERY as any,
          variables: {
            entityName,
          },
        })
        .then(result => {
          const info = result?.data?.entity?.organization?.name || entityName;
          if (isMounted()) {
            setOrgName(info);
          }
          return info;
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [skip]
  );
  if (!orgName) {
    return {loading: true, orgName: ''};
  }
  return {loading: false, orgName};
};

export const useCanAccessBilling = ({entityName}: {entityName: string}) => {
  const [canAccessBilling, setCanAccessBilling] = useState<boolean | null>(
    null
  );
  const isMounted = useIsMounted();
  const apolloClient = useApolloClient();
  const {userInfo} = useViewerInfo();

  useEffect(() => {
    if (!userInfo || !('username' in userInfo) || !userInfo.username) {
      return;
    }

    apolloClient
      .query({
        query: ORGANIZATION_BILLING_QUERY as any,
        variables: {entityName},
      })
      .then(result => {
        const organization = result?.data?.entity?.organization;

        if (!organization || !isMounted()) {
          return;
        }

        // Check if user is billing admin
        const isBillingUser =
          organization.billingUser?.username === userInfo.username;

        // Check if user is org admin
        // I believe admins also have access to billing - we can remove this and also the `members{...` part of the gql query above if not.
        const userMember = organization.members?.find(
          (member: any) => member.username === userInfo.username
        );
        const isOrgAdmin = userMember?.role === 'admin';

        setCanAccessBilling(isBillingUser || isOrgAdmin);
      })
      .catch(() => {
        if (isMounted()) {
          setCanAccessBilling(false);
        }
      });
  }, [entityName, userInfo, apolloClient, isMounted]);

  if (canAccessBilling === null) {
    return {loading: true, canAccessBilling: false};
  }

  return {loading: false, canAccessBilling};
};
